// T1.1a — parent-priors → starting-skill recommendation.
//
// The parent ticks abilities at /onboarding/kid/abilities ("Reads simple
// sentences", "Adds within 10", …). Each ability is stored on
// progress.parentPriors as `{ [slug]: true }`. This module turns those
// hints into a single RECOMMENDED STARTING SKILL that the daily mission
// (useTodaysMission) and the parent-results screen surface.
//
// IMPORTANT — a prior is an INITIAL POSITION HINT, not a mastery claim.
// recommendStartingSkill never writes to the mastery model; it only picks
// where to *point* the child first. The placement check (Tier 1b) still
// corrects an over-optimistic parent answer within minutes.
//
// CALIBRATION DISCLOSURE (per CLAUDE.md): ABILITY_TO_SKILLS and the
// "start one band beyond the most-advanced covered skill" rule below are
// REASONED DEFAULTS, not curriculum-expert-validated mappings. They are
// grounded in the microSkills.json prerequisite graph (the "covered" set
// is the transitive prerequisite closure of each ability's anchor skill),
// but the anchor choices and the band-step heuristic need expert review.

import microSkills from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };
import type { ParentPriors, AbilityResponse } from "@/App/progress";
import type { AgeBand } from "@/types/progression";
import { AGE_BAND_ORDER } from "@/adaptation/types";
// MA-CL-AB: shared band primitive — both serve and tree paths consume
// ageToBand/bandRank from one canonical source in src/engine/ageBand.ts.
import { ageToBand, bandRank } from "@/engine/ageBand.ts";

interface MicroSkillRow {
  id: string;
  label: string;
  ageBand: string;
  prerequisites: string[];
  strand: "math" | "reading" | "writing";
}

const SKILLS = microSkills as MicroSkillRow[];
const BY_ID: Record<string, MicroSkillRow> = Object.fromEntries(
  SKILLS.map((s) => [s.id, s]),
);
const INDEX: Record<string, number> = Object.fromEntries(
  SKILLS.map((s, i) => [s.id, i]),
);

// Curriculum order primary key: age-band rank, ascending. Skills inside a
// band are ordered by manifest index (the secondary key).

function orderKey(skillId: string): [number, number] {
  const s = BY_ID[skillId];
  return [s ? bandRank(s.ageBand) : AGE_BAND_ORDER.length, INDEX[skillId] ?? Number.MAX_SAFE_INTEGER];
}

function cmpOrder(a: string, b: string): number {
  const [ar, ai] = orderKey(a);
  const [br, bi] = orderKey(b);
  return ar !== br ? ar - br : ai - bi;
}

/** T1.4b: public AgeBand resolver. Null-safe wrapper around ageToBand:
 *  missing/non-finite age defaults to "5.5-6.0" (mid-product-window).
 *  Widely consumed across session/celebration/library screens and
 *  progression.ts's own age-scaling; the earlier `useStorybookPath`
 *  preview-window consumer named here is deleted (MA-343/ADR-099 — the
 *  Journey tab's chapter-scoped bound replaced age-scaled preview
 *  windows entirely, see journeyView.ts).
 *
 *  MA-CL-AB: delegates to ageToBand (src/engine/ageBand.ts) — the
 *  canonical shared primitive consumed by both serve and tree paths.
 *
 *  CALIBRATION TARGET: the "5.5-6.0" default is a design choice.
 *  Revisit if onboarding starts collecting precise birthdates. */
export function ageBandForStudent(studentAge: number | null | undefined): AgeBand {
  if (studentAge == null || !Number.isFinite(studentAge)) return "5.5-6.0";
  return ageToBand(studentAge);
}

type AbilityGroup = "reading" | "math";

interface AbilityOption {
  /** Stable slug persisted to progress.parentPriors as `{ [slug]: true }`. */
  slug: string;
  /** Parent-facing checkbox label. */
  label: string;
  group: AbilityGroup;
}

// P-ONBOARD-A — behavior-anchored questions per
// onboarding-science-deep-research.md. Each question asks about a SPECIFIC
// observable behavior the parent can answer ("Can your child read a simple
// word like cat on a page without help?") rather than a vague summary
// ("How is your child doing with reading?"). Research (Tier B evidence):
// behavior-anchored questions produce dramatically better measurement
// because parents CAN observe specific behaviors, even when they can't
// accurately summarize a broad level.
//
// Slugs are STABLE (persisted). Five are reused from T1.1a so existing
// accounts back-compat cleanly (knows_letter_sounds, reads_cvc,
// reads_simple_sentences, counts_to_10, adds_within_5). Five are NEW for
// the finer-grained anchors (hears_first_sound, blends_phonemes,
// counts_with_one_to_one_to_5, compares_more, names_numerals_1_to_10).
// Old slugs no longer asked (knows_letter_names, reads_short_stories,
// counts_to_20, adds_within_10, adds_within_20) are kept in
// ABILITY_TO_SKILLS below for legacy-account back-compat — the engine
// still consumes any prior the parent recorded under the old taxonomy.
export const ABILITY_OPTIONS: AbilityOption[] = [
  // Reading — 5 anchored questions, verbatim from the research.
  { slug: "knows_letter_sounds", label: "When you point to m, can your child usually say /m/?", group: "reading" },
  { slug: "hears_first_sound", label: "Can your child hear the first sound in sun?", group: "reading" },
  { slug: "blends_phonemes", label: "Can your child blend c-a-t to say cat?", group: "reading" },
  { slug: "reads_cvc", label: "Can your child read a simple word like cat on a page without help?", group: "reading" },
  { slug: "reads_simple_sentences", label: "Can your child read a very short sentence like I see a dog?", group: "reading" },
  // Math — 5 anchored questions, verbatim from the research.
  { slug: "counts_with_one_to_one_to_5", label: "Can your child count 5 objects, touching each one once?", group: "math" },
  { slug: "counts_to_10", label: "Can your child count 7 or 8 objects without skipping any?", group: "math" },
  { slug: "compares_more", label: "Can your child tell which small group has more without counting?", group: "math" },
  { slug: "names_numerals_1_to_10", label: "Can your child name numerals 1–10?", group: "math" },
  { slug: "adds_within_5", label: "Can your child solve 3 + 1 using fingers or objects?", group: "math" },
];

// ability slug → the "anchor" microSkill the ability implies the child can
// already do (the most-advanced demonstrated skill). recommendStartingSkill
// takes the transitive prerequisite closure of the anchor as the covered
// set and then walks the prerequisite graph one step FORWARD. CALIBRATION
// TARGET — anchor choices are reasoned defaults pending curriculum review.
//
// P-ONBOARD-A: 5 new slugs added for the behavior-anchored questions
// (hears_first_sound, blends_phonemes, counts_with_one_to_one_to_5,
// compares_more, names_numerals_1_to_10). 5 old slugs kept for legacy
// account back-compat — kids onboarded under T1.1a have priors stored
// under those keys; the engine still consumes them correctly here even
// though the new survey doesn't ask those exact questions any more.
export const ABILITY_TO_SKILLS: Record<string, string[]> = {
  // ── P-ONBOARD-A anchored questions (in survey render order) ─────────
  knows_letter_sounds: ["ms.read.02"], // → Sounds to letters (ms.read.04)
  hears_first_sound: ["ms.read.02"], // → Sounds to letters (ms.read.04; phonemic-awareness precursor anchors here too)
  blends_phonemes: ["ms.read.06"], // → CVC encoding (blending IS the gateway to decoding)
  reads_cvc: ["ms.read.06"], // → CVC encoding
  reads_simple_sentences: ["ms.read.10"], // → Academic vocabulary
  counts_with_one_to_one_to_5: ["ms.math.00b"], // → Subitizing (1:1 correspondence is the cardinality precursor)
  counts_to_10: ["ms.math.00b"], // → Subitizing
  compares_more: ["ms.math.02"], // → Comparing quantities (direct match)
  names_numerals_1_to_10: ["ms.math.00b"], // → Subitizing (numeral recognition is part of the early-counting bundle)
  adds_within_5: ["ms.math.07"], // → Subtraction within 5
  // ── Legacy slugs (T1.1a) — kept for back-compat. Not asked any more
  //    but recommendStartingSkill still consumes them if they're present
  //    on an existing kid's persisted priors.
  knows_letter_names: ["ms.read.01"], // → Letter sounds
  reads_short_stories: ["ms.read.13"], // → Silent letters
  counts_to_20: ["ms.math.02"], // → Comparing quantities
  adds_within_10: ["ms.math.09"], // → Teen numbers / place value
  adds_within_20: ["ms.math.11"], // → Missing addends within 20
};

// ── P-ONBOARD-A: response → prior mapping ───────────────────────────
//
// Each abilities-survey question collects one of 4 responses. We persist
// TWO sibling keys per slug: the boolean `<slug>` (true only when the
// parent picked "Usually on their own", matching the existing engine
// rule that `tickedAbilitySlugs` reads `=== true`) and a rich confidence
// label `<slug>_confidence` capturing the full 4-way response.
//
// THIS PR's engine consumption is unchanged — only `true` slugs influence
// recommendStartingSkill (preserved by the `=== true` filter). The
// confidence label is bedrock for P-ONBOARD-B's band-shift calibration:
//   - "usually"   → high confidence kid CAN do it → emerging-or-secure prior
//   - "sometimes" → moderate confidence (ambiguous — kid does some/sometimes)
//   - "not_yet"   → high confidence kid CANNOT do it → engine should later
//                   serve foundational items for this skill (deferred to
//                   P-ONBOARD-B; today the slug is just not-ticked)
//   - "unsure"    → low confidence; engine should rely on first-session
//                   calibration evidence rather than this prior (today,
//                   functionally identical to a missing answer)
//
// Returns the two values the caller writes alongside each other. Always
// writes BOTH keys so the persisted shape is uniformly structured
// (parent-results / future engine reads can rely on the sibling pattern).
interface PriorEntry {
  /** Value the engine reads via `=== true`. */
  value: boolean;
  /** Full 4-way response, captured for P-ONBOARD-B. */
  confidence: AbilityResponse;
}

export function responseToPrior(response: AbilityResponse): PriorEntry {
  return {
    value: response === "usually",
    confidence: response,
  };
}

/** Read the confidence label for a slug from persisted priors, with
 *  legacy back-compat: a T1.1a-era prior stored as `{ slug: true }` with
 *  NO `<slug>_confidence` sibling is reported as "usually" (the parent
 *  ticked it, so they were confident). Returns null when the slug has
 *  no answer at all (skipped). */
export function confidenceFor(
  priors: ParentPriors | null | undefined,
  slug: string,
): AbilityResponse | null {
  if (!priors || typeof priors !== "object") return null;
  const conf = priors[`${slug}_confidence`];
  if (
    conf === "usually" ||
    conf === "sometimes" ||
    conf === "not_yet" ||
    conf === "unsure"
  ) {
    return conf;
  }
  // Legacy back-compat: ticked-without-confidence means "usually".
  if (priors[slug] === true) return "usually";
  return null;
}

/** Calibration: median onboarding age, used only when no priors AND no age
 *  are available (recommendation falls back to the age-default branch). */
export const PRIORS_DEFAULT_AGE = 5.0;

function tickedAbilitySlugs(priors: ParentPriors | null | undefined): string[] {
  if (!priors || typeof priors !== "object") return [];
  // Only `true` ability slugs count. Legacy named fields ("yes"/"no"/
  // "not_sure", read by placementEngine.applyPriorBoost) are ignored here.
  return Object.keys(priors).filter(
    (k) => priors[k] === true && ABILITY_TO_SKILLS[k] !== undefined,
  );
}

/** Skills the priors imply the child can already do = the transitive
 *  prerequisite closure of every ticked ability's anchor skills. */
export function impliedCoveredSkills(
  priors: ParentPriors | null | undefined,
): Set<string> {
  const covered = new Set<string>();
  const stack: string[] = [];
  for (const slug of tickedAbilitySlugs(priors)) {
    stack.push(...(ABILITY_TO_SKILLS[slug] ?? []));
  }
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (covered.has(id) || !BY_ID[id]) continue;
    covered.add(id);
    for (const p of BY_ID[id].prerequisites) stack.push(p);
  }
  return covered;
}

interface RecommendInput {
  priors?: ParentPriors | null;
  /** Numeric age in years (progress.studentAge). */
  ageYears?: number | null;
  /** Most-recent attempted skill, if the child has practiced before. */
  lastAttemptedSkill?: string | null;
  /** Track I.1.b — diagnostic placement signal. `strand` is the diagnostic's
   *  subject (the onboarding diagnostic is "math"); `accuracy` in [0,1];
   *  `total` is the number of diagnostic items actually answered — REQUIRED
   *  so a skipped/empty diagnostic (total 0, where accuracy degenerates to 0)
   *  reads as NO EVIDENCE instead of a "<0.5 → demote" signal. When present
   *  (and no real session history), nudges the priors/age base one skill
   *  ahead/back within the matching strand. */
  placement?: { strand: "math" | "reading" | "writing"; accuracy: number; total: number } | null;
  /** MA-301 — reading-strand counterpart of `placement`. Kept as a SEPARATE
   *  field (not a rename/union) so the ~38 existing `placement:` call sites
   *  (all math) are untouched. `applyPlacementBias` no-ops on a strand
   *  mismatch and a base skill is always exactly one strand, so applying
   *  both in sequence is safe — at most one of the two ever actually
   *  biases a given call. */
  readingPlacement?: { strand: "math" | "reading" | "writing"; accuracy: number; total: number } | null;
  /** C-1 — the onboarding diagnostic's subject. When set, the no-priors age
   *  fallback (path c) prefers this strand so a 6.5-7.0 child (whose MATH band
   *  is empty) isn't seeded onto a reading skill, and an empty strand-band is
   *  clamped DOWN to the nearest lower authored band of that strand instead of
   *  jumping to the global top (which seeded a 6yo onto grade-3 regrouping).
   *  Omitted → byte-identical legacy (strand-blind) behavior. */
  subject?: "math" | "reading" | "writing" | null;
}

// Track I.1.b — shift a skill one step ahead (+1) / back (-1) within its own
// strand, ordered by cmpOrder; clamped at the strand ends (returns the input
// unchanged at index 0 / last, or if the id isn't a known skill).
export function shiftSkillInStrand(skillId: string, dir: 1 | -1): string {
  const row = BY_ID[skillId];
  if (!row) return skillId;
  const inStrand = SKILLS.filter((s) => s.strand === row.strand).sort((a, b) =>
    cmpOrder(a.id, b.id),
  );
  const idx = inStrand.findIndex((s) => s.id === skillId);
  if (idx < 0) return skillId;
  const next = idx + dir;
  if (next < 0 || next >= inStrand.length) return skillId; // clamp at the ends
  return inStrand[next].id;
}

// Track I.1.b — nudge the priors/age base by the diagnostic placement, but
// ONLY when the diagnostic's strand matches the base skill's strand (a math
// diagnostic must not move a reading start). >=0.8 (4-5 of 5) -> one step
// ahead; <0.5 (0-2 of 5) -> one step back; 3/5 -> on level. Thresholds tuned
// to the 5-item diagnostic granularity (calibration; revisit with field data).
export function applyPlacementBias(
  base: string,
  placement: RecommendInput["placement"],
): string {
  if (!placement) return base;
  // NO EVIDENCE ≠ 0% — a skipped/empty diagnostic produces accuracy 0 from
  // total 0 (diagnosticPlacement.ts guards the divide-by-zero with 0). That
  // record is honest telemetry ("diagnostic skipped") but must not demote the
  // age/priors default. Caught live by e2e 19-parent-priors: the no-priors
  // 5yo default (ms.math.02) was demoted to Subitizing on a 0-item check-in.
  if (!(placement.total > 0)) return base;
  const row = BY_ID[base];
  if (!row || row.strand !== placement.strand) return base;
  if (placement.accuracy >= 0.8) return shiftSkillInStrand(base, 1);
  if (placement.accuracy < 0.5) return shiftSkillInStrand(base, -1);
  return base;
}

type StoredPlacementResult = { accuracy?: unknown; total?: unknown } | null | undefined;

/** MA-301 — one shared guard for turning a stored diagnostic result
 *  (`progress.placementResults` / `progress.readingPlacementResults`) into
 *  a `RecommendInput` placement arg, used by BOTH real callers
 *  (`useTodaysMission.ts`, `onboarding.parent-results.tsx`) so the
 *  guard shape can't drift between them. Mirrors `applyPlacementBias`'s own
 *  "total must be present so a skipped/0-item diagnostic reads as no
 *  evidence" contract. */
export function placementFromStoredResult(
  stored: StoredPlacementResult,
  strand: "math" | "reading",
): { strand: "math" | "reading"; accuracy: number; total: number } | null {
  if (
    stored &&
    typeof stored.accuracy === "number" &&
    typeof stored.total === "number"
  ) {
    return { strand, accuracy: stored.accuracy, total: stored.total };
  }
  return null;
}

/** MA-309 — the onboarding Starting-level pre-highlight (easy/adaptive/
 *  hard) derives from the REAL diagnostic placement result (accuracy over
 *  an actually-answered set), never from a live mastery score presented as
 *  a diagnostic outcome (that read as a fabricated "check-in %" on a fresh
 *  profile with zero attempts). No evidence (skipped/0-item diagnostic,
 *  `total` not > 0) makes no claim either way — defaults "adaptive". */
export function bandFromPlacement(
  placement: { accuracy: number; total: number } | null,
): "easy" | "adaptive" | "hard" {
  if (!placement || !(placement.total > 0)) return "adaptive";
  if (placement.accuracy >= 0.8) return "hard";
  if (placement.accuracy < 0.5) return "easy";
  return "adaptive";
}

/**
 * Pick where to start the child (public entry):
 *   - real session history (lastAttemptedSkill) wins and is NOT placement-
 *     biased (a practiced kid is past placement);
 *   - otherwise the priors/age base (recommendStartingSkillBase) is nudged by
 *     the diagnostic placement seed (Track I.1.b).
 * Returns a microSkill id (always a real id present in microSkills.json).
 */
export function recommendStartingSkill(input: RecommendInput): string {
  const base = recommendStartingSkillBase(input);
  // Don't bias when real session history drove the base (matches the (a)
  // early-return condition in recommendStartingSkillBase byte-for-byte).
  if (input.lastAttemptedSkill && BY_ID[input.lastAttemptedSkill]) return base;
  // MA-301: apply both strand placements in sequence — each is a no-op
  // unless it matches `base`'s own strand, so at most one ever fires.
  const biased = applyPlacementBias(
    applyPlacementBias(base, input.placement),
    input.readingPlacement,
  );

  // MA-130 safety net: applyPlacementBias's +-1 in-strand shift runs AFTER
  // the priors-anchor clamp above and is itself unclamped, so a strong (or
  // weak) placement result can walk an already-on-band base back out of
  // the child's own age band (verified: a weak-placement demote off a
  // clamped on-band base can cross one band below). Re-clamp the FINAL
  // (post-bias) result the same way, but ONLY when priors actually drove
  // the base (recommendStartingSkillBase's branch (b)) -- branch (c)'s
  // pure age-default base is a different, pre-existing path this item
  // does not change (see the P-block in parentPriors.test.mjs: a
  // placement-driven demote off the age-default base intentionally can
  // cross one band below on real struggle evidence).
  if (
    impliedCoveredSkills(input.priors).size > 0 &&
    input.ageYears != null &&
    Number.isFinite(input.ageYears)
  ) {
    const childRank = bandRank(ageToBand(input.ageYears));
    const biasedRow = BY_ID[biased];
    if (biasedRow && bandRank(biasedRow.ageBand) !== childRank) {
      return clampToStrandFrontier(biasedRow.strand, input.ageYears, biased);
    }
  }
  return biased;
}

/**
 * Pick where to start the child (priors/age core — no placement bias):
 *   (a) if they've attempted a skill before, resume it (real signal wins);
 *   (b) else if priors exist, walk the prerequisite graph one step forward
 *       from the most-advanced covered skill (the "anchor"): take the
 *       lowest-order skill in the anchor's strand that directly builds on
 *       the anchor (lists it as a prerequisite) and whose other
 *       prerequisites are all covered. Falls back to the next prerequisite-
 *       ready skill, then to the anchor itself;
 *   (c) else fall back to the first skill in the band that contains the
 *       child's age.
 *
 * Returns a microSkill id (always a real id present in microSkills.json).
 */
function recommendStartingSkillBase(input: RecommendInput): string {
  // (a) Real signal beats priors.
  if (input.lastAttemptedSkill && BY_ID[input.lastAttemptedSkill]) {
    return input.lastAttemptedSkill;
  }

  // (b) Priors → walk one prerequisite step beyond the covered frontier.
  const covered = impliedCoveredSkills(input.priors);
  if (covered.size > 0) {
    let anchor: string | null = null;
    for (const id of covered) {
      if (anchor === null || cmpOrder(id, anchor) > 0) anchor = id;
    }
    const anchorRow = anchor ? BY_ID[anchor] : null;
    if (anchorRow && anchor) {
      const strand = anchorRow.strand;
      const prereqsCovered = (s: MicroSkillRow) =>
        s.prerequisites.every((p) => covered.has(p));

      // Primary: skills that directly build on the anchor and whose other
      // prerequisites the child already covers.
      const directUnlocks = SKILLS.filter(
        (s) =>
          s.strand === strand &&
          !covered.has(s.id) &&
          s.prerequisites.includes(anchor!) &&
          prereqsCovered(s),
      ).sort((a, b) => cmpOrder(a.id, b.id));

      // Fallback: the next prerequisite-ready skill after the anchor.
      const frontier = SKILLS.filter(
        (s) =>
          s.strand === strand &&
          !covered.has(s.id) &&
          cmpOrder(s.id, anchor!) > 0 &&
          prereqsCovered(s),
      ).sort((a, b) => cmpOrder(a.id, b.id));

      const priorsPick = directUnlocks[0]?.id ?? frontier[0]?.id ?? anchor;

      // MA-130: the priors-anchor walk above reasons ONLY over the
      // prerequisite graph implied by ticked abilities — it is age-blind
      // by construction, so it can land a band below OR above the child's
      // actual age. When a real age is known, clamp into the child's own
      // band (reuses ageFrontierSkillForStrand via clampToStrandFrontier
      // below — the same on-ramp the tree/serve age cover already use,
      // ADR-029/034 — so the fresh-learner start and the tree frontier
      // agree by construction). Unclamped when ageYears is null/unknown
      // (byte-identical legacy behavior — the priors-only unit tests
      // deliberately omit age to test this walk in isolation).
      if (input.ageYears != null && Number.isFinite(input.ageYears)) {
        const childRank = bandRank(ageToBand(input.ageYears));
        const pickRow = BY_ID[priorsPick];
        if (pickRow && bandRank(pickRow.ageBand) !== childRank) {
          return clampToStrandFrontier(strand, input.ageYears, priorsPick);
        }
      }
      return priorsPick;
    }
  }

  // (c) No priors → first skill in the age-appropriate band. When a SUBJECT
  //     is known (C-1: the onboarding diagnostic's strand), prefer that strand
  //     so a 6.5-7.0 child — whose math band is EMPTY — isn't seeded onto a
  //     reading skill. With no subject this is byte-identical to before.
  const age = input.ageYears ?? PRIORS_DEFAULT_AGE;
  const band = ageToBand(age);
  const subject = input.subject ?? null;
  const inStrand = (s: MicroSkillRow) => subject == null || s.strand === subject;

  const inBand = SKILLS.filter((s) => s.ageBand === band && inStrand(s)).sort(
    (a, b) => cmpOrder(a.id, b.id),
  );
  if (inBand.length > 0) return inBand[0].id;

  // C-1: an empty band FOR THIS STRAND (the math 6.5-7.0 hole) clamps DOWN to
  // the nearest lower authored band of the SAME strand — never up to the
  // global top (which seeded a 6yo onto grade-3 multi-digit regrouping).
  if (subject != null) {
    const targetRank = bandRank(band);
    const lower = SKILLS.filter(
      (s) => s.strand === subject && bandRank(s.ageBand) < targetRank,
    );
    if (lower.length > 0) {
      const nearestRank = Math.max(...lower.map((s) => bandRank(s.ageBand)));
      const nearest = lower
        .filter((s) => bandRank(s.ageBand) === nearestRank)
        .sort((a, b) => cmpOrder(a.id, b.id));
      if (nearest.length > 0) return nearest[0].id;
    }
  }

  // Fallback (no subject, or no lower strand band — e.g. age 7.0 maps to
  // "7.0+", which has no skills): the lowest-order skill of the most-advanced
  // band present.
  const highestRank = Math.max(...SKILLS.map((s) => bandRank(s.ageBand)));
  const top = SKILLS.filter((s) => bandRank(s.ageBand) === highestRank).sort(
    (a, b) => cmpOrder(a.id, b.id),
  );
  return top[0]?.id ?? "ms.math.09";
}

/** AGE-GATE: skills the child has clearly aged out of — those whose CONTENT
 *  age band sits AGE_MASTERY_GAP-or-more bands below the child's own band.
 *  buildTodaysMission's chapter walk is age-BLIND (it picks the lowest
 *  mastery-band skill in the first incomplete chapter), so without this a
 *  7-year-old with any chapter-1 progress is handed ms.read.01 ("tap the
 *  letter m", a 4.5-5.0 Pre-K item). Marking these mastered makes the walk
 *  skip them. Gap = 4 is deliberately conservative: ms.read.01 (4.5-5.0,
 *  rank 1) is only masked once the child's band rank >= 5, i.e. age 6.5+.
 *  A child < 6.5 (incl. every age-6 fixture/kid) keeps letter naming, so
 *  existing placement + e2e behaviour is unchanged; a 6.5-7yo+ skips the
 *  Pre-K/early-K content they've long since mastered. Pure + age-only — does
 *  NOT override a skill the child has actually practiced (that union happens
 *  in withAgeImpliedMastery, where the served pool forces fluent). */
export const AGE_MASTERY_GAP = 4;
export function ageImpliedMasteredSkillIds(
  studentAge: number | null | undefined,
): string[] {
  const kidRank = bandRank(ageBandForStudent(studentAge));
  return SKILLS.filter((s) => bandRank(s.ageBand) <= kidRank - AGE_MASTERY_GAP).map(
    (s) => s.id,
  );
}

/** SKILL-TREE FRONTIER. The parent skill tree COVERS skills below the
 *  child's band so the open-circle frontier lands on the child's own age
 *  band — "Now" reads as the on-age skill, not one band behind.
 *
 *  MA-85 (ADR-107): this set is BAND-ONLY — it never reads mastery or
 *  attempts. Membership is the cover (chain-alive in the fold, masked as done
 *  by the serve walk via ageCoveredForServeSkillIds), not a chip: a covered
 *  skill reads "For younger ages" (`age_covered`) only when the child has
 *  never attempted it; attempted and not secured it reads "Not yet"
 *  (`not_yet`), secured it reads its tier ("Almost mastered"/"Mastered"),
 *  see skillTreeStatus.statusFor.
 *
 *  MA-P0-9 / ADR-034 (2026-06-28, founder decision): TREE_AGE_COVER_GAP was
 *  previously 1 ("gentle ramp — frontier one band below age"). Two live bugs
 *  showed that GAP=1 is wrong:
 *    (A) Sasha (6.0yo) saw the frontier on ms.math.06 "Compose & decompose to
 *        10" (5.5-6.0, one band BELOW her band) — the founder wants the
 *        frontier ON the child's selected band.
 *    (B) The tree was internally incoherent: ms.math.09 "Addition within 10"
 *        showed SECURE (now "Almost mastered") while ms.math.07 "Addition within 5" (a REQUIRED prereq
 *        of ms.math.09, same 5.5-6.0 band) showed LOCKED — because the linear
 *        fold gave ms.math.06 "next" (first non-covered) and then
 *        prevMastered=false, locking the sibling ms.math.07/08 even though
 *        their shared prereq ms.math.05 was covered. Mastery data then "won"
 *        for ms.math.09 (secure > age_covered) but left ms.math.07 locked.
 *  GAP=0 (cover everything strictly below the child's band) fixes both: all
 *  5.5-6.0 skills are covered for a 6.0yo, the fold flows cleanly, and the
 *  frontier lands on the 6.0-6.5 band. `ageCoveredForServeSkillIds`
 *  delegates here, so the served set also obeys the child's selected band.
 *  Null/unknown age → empty set (no covering, unchanged behaviour). */
export const TREE_AGE_COVER_GAP = 0;
export function ageCoveredForTreeSkillIds(
  studentAge: number | null | undefined,
): string[] {
  if (studentAge == null || !Number.isFinite(studentAge)) return [];
  const kidRank = bandRank(ageBandForStudent(studentAge));
  return SKILLS.filter((s) => bandRank(s.ageBand) < kidRank - TREE_AGE_COVER_GAP).map(
    (s) => s.id,
  );
}

/** AGE-FIT SERVE COVER (ADR-028) — make the SESSION serve path obey the SAME
 *  age-cover the parent skill tree uses to badge skills "For younger ages".
 *
 *  THE BUG THIS CLOSES (verified live, Sasha 6.0-6.5): the serve walk's only
 *  age guard was `ageImpliedMasteredSkillIds` (AGE_MASTERY_GAP = 4 — masks
 *  content >= 4 bands BELOW the child). But the tree badges "younger ages"
 *  with the much tighter `ageCoveredForTreeSkillIds` (TREE_AGE_COVER_GAP was 1
 *  then — content > 1 band below; it is 0 since ADR-034, so today the cover
 *  is every skill strictly below the child's band). The two gaps disagreed by
 *  THREE bands,
 *  so a 6.0-6.5 child (band rank 4) was *served* "Count objects to 10" /
 *  "Subitizing" (ms.math.00b / ms.math.01, band 4.5-5.0, rank 1 — 3 bands
 *  below) even though the tree already labelled exactly those skills
 *  FOR-YOUNGER-AGES. #328's secure-prereq closure could not fix it: it only
 *  fires when a DOWNSTREAM skill is already SECURE, and Sasha has secured
 *  nothing yet, so the closure is empty and the age-blind chapter walk lands
 *  on the lowest-band early skill.
 *
 *  THE FIX: the serve cover IS the tree cover. This function DELEGATES to
 *  `ageCoveredForTreeSkillIds` so the served set and the labelled set are
 *  identical BY CONSTRUCTION and can never drift (one TREE_AGE_COVER_GAP
 *  governs both). useTodaysMission unions the result into withAgeImpliedMastery
 *  (pick-only, ephemeral — never persisted, never reaches the dashboard), so
 *  the walk treats every "younger ages" skill as done and routes the child to
 *  age-appropriate work. The invariant: a skill the tree badges FOR-YOUNGER-
 *  AGES is NEVER served as the focal NOW skill. (This COMPLEMENTS ADR-029's
 *  per-strand CTA cover, #332: that covers the non-focal "resume" card path,
 *  this covers the focal chapter walk — together every /play entry obeys the
 *  serve↔label invariant.)
 *
 *  SAFETY / NON-STRANDING: this is a PICK-ONLY mask on the chapter walk, not a
 *  curriculum change. It only ever fires on a child WITH progress (the
 *  empty-states branch still routes through the age-aware
 *  recommendStartingSkill, so a genuine fresh 6yo is *placed* age-appropriately
 *  rather than masked). buildTodaysMission stays total (cases b/c) and the RC-R
 *  served-pool gate guarantees a renderable pool, so the child is never
 *  stranded; the demotion valve still provides a path DOWN for a genuinely
 *  mis-placed learner. AGE_MASTERY_GAP = 4 is deliberately LEFT UNCHANGED — the
 *  age-6 e2e fixtures that pin it (and seed empty skillStates) are untouched,
 *  and this cover is an ADDITIVE union element.
 *
 *  Null/unknown age -> empty set (cover inactive — exact prior behaviour). */
export function ageCoveredForServeSkillIds(
  studentAge: number | null | undefined,
): string[] {
  return ageCoveredForTreeSkillIds(studentAge);
}

/** AGE-FRONTIER ON-RAMP (ADR-029) — the gentlest skill in `strand` that the
 *  tree does NOT badge "For younger ages" for this child, i.e. the lowest
 *  authored band at or above the child's cover frontier. Used as the
 *  per-strand CTA fallback when the static manifest default is itself
 *  tree-covered for an OLDER child (e.g. a 7yo whose ms.math.09 default sits
 *  >1 band below their age). Pure + age-only; returns null only for an unknown
 *  strand. For a null/young age the cover is empty, so this returns the
 *  strand's earliest skill — callers that want the calibration default for
 *  that case should guard on `ageCoveredForTreeSkillIds(...).includes(default)`
 *  first (pickStrandSkill does). */
export function ageFrontierSkillForStrand(
  strand: string,
  studentAge: number | null | undefined,
): string | null {
  const covered = new Set(ageCoveredForTreeSkillIds(studentAge));
  const candidates = SKILLS.filter(
    (s) => s.strand === strand && !covered.has(s.id),
  );
  if (candidates.length === 0) return null;
  const lowestRank = Math.min(...candidates.map((s) => bandRank(s.ageBand)));
  return (
    candidates
      .filter((s) => bandRank(s.ageBand) === lowestRank)
      .sort((a, b) => cmpOrder(a.id, b.id))[0]?.id ?? null
  );
}

/** MA-130 — clamp helper for the fresh-learner start (used on both the
 *  priors-anchor base and the placement-biased result). Prefers
 *  `ageFrontierSkillForStrand`'s on-ramp; when the strand has NO content
 *  at/above the child's band (e.g. reading ceilings out at 7.5+ while some
 *  older fresh learners exceed it, so the frontier is null), degrades to
 *  the same "highest authored band present" fallback
 *  `recommendStartingSkillBase`'s branch (c) already uses, rather than
 *  silently returning the unclamped input — which would defeat the clamp
 *  for exactly the oldest, most-advanced fresh learners it matters most
 *  for. */
function clampToStrandFrontier(
  strand: string,
  ageYears: number,
  fallback: string,
): string {
  const frontier = ageFrontierSkillForStrand(strand, ageYears);
  if (frontier) return frontier;
  const strandSkills = SKILLS.filter((s) => s.strand === strand);
  if (strandSkills.length === 0) return fallback;
  const highestRank = Math.max(...strandSkills.map((s) => bandRank(s.ageBand)));
  const top = strandSkills
    .filter((s) => bandRank(s.ageBand) === highestRank)
    .sort((a, b) => cmpOrder(a.id, b.id));
  return top[0]?.id ?? fallback;
}

/** AGE PLACEMENT FLOOR — the gentler companion to the age-skip GATE above.
 *
 *  THE GAP THE GATE LEAVES: ageImpliedMasteredSkillIds only masks skills
 *  >= AGE_MASTERY_GAP (4) bands BELOW the child — a hard, far-below cliff.
 *  Anything in the 1-3-band-below window is left fully in play, so
 *  buildTodaysMission's (mastery-band-ranked, age-BLIND) chapter walk can
 *  still hand an older child a focal skill 2-3 bands beneath their age
 *  (e.g. a 7yo with early-chapter progress landing on a 5.0-5.5 / ms.math.01
 *  class item — too easy, but not far enough below to be skip-gated).
 *
 *  THE FLOOR: skills whose CONTENT band sits more than AGE_FLOOR_GAP bands
 *  below the child's age band are "below floor" — buildTodaysMission should
 *  DE-PRIORITISE (not delete) them when ranking focal candidates, preferring
 *  any reachable at-or-above-floor candidate first. It is a SOFT re-rank, not
 *  a mask: if every reachable candidate is below floor (a brand-new learner,
 *  or a child whose only unlocked content is genuinely early), the below-floor
 *  candidates still rank normally so the session is never stranded empty.
 *
 *  CALIBRATION — AGE_FLOOR_GAP = 3 (default): catches content ~1.5+ bands below age (clearly too easy); deliberately MORE CONSERVATIVE
 *  (smaller) than the skip gate's AGE_MASTERY_GAP = 4:
 *    - The skip gate is a DESTRUCTIVE mask (forces fluent, removes the skill
 *      from the walk), so it must be cautious — 4 bands keeps every age-6
 *      fixture/kid untouched.
 *    - The floor is NON-DESTRUCTIVE (re-rank only, never strands), so it can
 *      bite sooner: 2 bands means a child is only nudged off a focal skill
 *      that is 3+ bands below them, while still being allowed onto anything
 *      within 2 bands (normal review/consolidation range). Worked example at
 *      AGE_BAND_ORDER ranks: a 7.25yo (band "7.0-7.5", rank ~9) floors out
 *      skills with band rank <= 9 - 1 - 2 = 6 (i.e. <= "6.0-6.5"); a 5.5yo
 *      (rank ~5) floors out only band rank <= 2 (<= "5.0-5.5"), leaving all
 *      their on-level early content fully in play. A brand-new learner with
 *      only chapter-1 (rank 0-1) content reachable has NOTHING at/above floor,
 *      so the floor no-ops and current behaviour is preserved.
 *  Like AGE_MASTERY_GAP, this is a REASONED DEFAULT pending curriculum review,
 *  not an expert-validated constant.
 *
 *  INTERACTION with enrich-don't-accelerate: the floor is a FLOOR on focal
 *  SELECTION among already-reachable/unlocked candidates — it never unlocks a
 *  chapter early, never skips an unmet prerequisite chain, and never forces a
 *  grade-skip. It only changes WHICH of the skills the child can already legally
 *  reach is chosen as focal, so the acceleration/enrichment shapes downstream
 *  (sessionRun, acceleration rules) are untouched.
 */
export const AGE_FLOOR_GAP = 3;

/** Skills that sit BELOW the age placement floor for this child: content whose
 *  age-band rank is more than AGE_FLOOR_GAP bands below the child's own band
 *  rank. buildTodaysMission de-prioritises (does NOT remove) these among focal
 *  candidates. Returns a Set for O(1) membership. Pure + age-only.
 *
 *  Null/unknown age → empty set (same safe no-op posture as the skip gate's
 *  null branch: never floor a child whose age we couldn't resolve). */
/** Skills whose content age-band sits STRICTLY ABOVE the child's own band.
 *  The floor promotes a child TOWARD their age, never PAST it — a 5.5yo must
 *  not be floated up to a 6.5-7.0 skill just because it's prereq-ready. Empty
 *  when age is null (floor inactive). */
export function ageAboveBandSkillIds(
  studentAge: number | null | undefined,
): Set<string> {
  if (studentAge == null || !Number.isFinite(studentAge)) return new Set();
  const kidRank = bandRank(ageBandForStudent(studentAge));
  return new Set(
    SKILLS.filter((s) => bandRank(s.ageBand) > kidRank).map((s) => s.id),
  );
}

/** MA-86/MA-454 — the curriculum's TERMINAL age band: the open-ended top
 *  band the age→band resolver emits for every child at/above its lower
 *  edge. A child in this band can never "age into" the bands authored
 *  above it ("8.0-8.5" is resolver-dormant — ageToBand never emits it), so
 *  without a tolerance the content banded there (ms.math.27/28) is
 *  permanently unreachable through the engine's own serve walk. */
export const TERMINAL_AGE_BAND: AgeBand = "7.5+";

/** SERVE-PATH above-band set (MA-86 mastery lift / MA-454 terminal-band
 *  tolerance). Identical to `ageAboveBandSkillIds` — the MA-86 serve
 *  ceiling's membership set — EXCEPT for a child in the TERMINAL age band
 *  ("7.5+"): there the exclusion threshold is `kidRank + 1` instead of
 *  `kidRank`, so the one band authored above the terminal band ("8.0-8.5",
 *  ms.math.27/28) reads as ON-band for a 7.5+ child only. Rationale: the
 *  resolver never emits "8.0-8.5" (the open-ended "7.5+" row matches
 *  first), so a terminal-band child can never age past the ceiling the way
 *  every younger child does — +1 here is the ceiling analogue of what
 *  aging up does for everyone else, and it is net-consistent: the live-QA
 *  net's belowBand currency (MA-86b) already passes childRank+1.
 *
 *  SERVE-PATH ONLY: the parent skill tree keeps `ageAboveBandSkillIds`
 *  (MA-91/ADR-042 — tree "Ahead" labelling is a communication surface and
 *  deliberately stays strict), so a 7.5+ child's tree still labels
 *  ms.math.27/28 "Ahead" while the serve walk may reach them. That
 *  tree/serve divergence for terminal-band children is deliberate and
 *  recorded in the MA-86 mastery-lift ADR.
 *
 *  HARDENING: the tolerance is additionally gated on `studentAge >= 7.5`
 *  explicitly, because ageToBand CLAMPS invalid sub-minimum ages (e.g. a
 *  corrupt 0/negative age, which is < "4.0-4.5"'s lower edge) to "7.5+"
 *  too — those must NOT inherit the +1 tolerance (they already get the
 *  wrong band; widening their ceiling would compound the corruption).
 *  Null/unknown age → empty set (ceiling inactive, exactly like
 *  ageAboveBandSkillIds). */
export function serveAboveBandSkillIds(
  studentAge: number | null | undefined,
): Set<string> {
  if (studentAge == null || !Number.isFinite(studentAge)) return new Set();
  const kidBand = ageBandForStudent(studentAge);
  const kidRank = bandRank(kidBand);
  const ceilingRank =
    kidBand === TERMINAL_AGE_BAND && studentAge >= 7.5 ? kidRank + 1 : kidRank;
  return new Set(
    SKILLS.filter((s) => bandRank(s.ageBand) > ceilingRank).map((s) => s.id),
  );
}


export function ageFloorBelowSkillIds(
  studentAge: number | null | undefined,
): Set<string> {
  if (studentAge == null || !Number.isFinite(studentAge)) return new Set();
  const kidRank = bandRank(ageBandForStudent(studentAge));
  return new Set(
    SKILLS.filter((s) => bandRank(s.ageBand) < kidRank - AGE_FLOOR_GAP).map(
      (s) => s.id,
    ),
  );
}


/** Age-floor READINESS guard. The floor must never promote a skill ABOVE an
 *  unmet prerequisite. A skill is floor-eligible only if every prerequisite is
 *  already mastered (secure/fluent). Without this, a 6yo with no reading
 *  progress would be floated from ms.read.01 "Letter names" (no prereqs, below
 *  floor) up to ms.read.03 "Letter formation" (at floor) even though its
 *  prereq ms.read.01 is not done. `masteryBandFor` returns the child's mastery
 *  band for a skill id (undefined when never attempted → treated as unmet). */
export function prerequisitesSatisfied(
  skillId: string,
  masteryBandFor: (id: string) => string | undefined,
): boolean {
  const row = BY_ID[skillId];
  if (!row) return true;
  return row.prerequisites.every((pid) => {
    const mb = masteryBandFor(pid);
    return mb === "secure" || mb === "fluent";
  });
}


/** OPTION B — EVIDENCE-BASED PREREQUISITE SKIP (ADR-027).
 *
 *  The session serve walk (buildTodaysMission) is age-BLIND: it picks the
 *  lowest-mastery-band skill in the first incomplete chapter and nothing told
 *  it that a SECURED downstream skill implies its upstream prerequisites are
 *  already done. So a child whose "Addition within 10" (ms.math.09) is SECURE
 *  was still served its prerequisites "Subitizing" (ms.math.01) and "Count
 *  objects to 10" (ms.math.00b) as the NOW skill — the exact skills the parent
 *  tree badges "For younger ages". (See docs/adr/027.)
 *
 *  This returns the transitive PREREQUISITE CLOSURE of every skill the child
 *  has SECURED (mastery band secure/fluent), EXCLUDING any skill that is itself
 *  already secured (those keep their own real state in the walk). Feeding this
 *  set through the SAME withAgeImpliedMastery mechanism marks those
 *  prerequisites ephemerally-fluent for the PICK ONLY, so the walk skips them
 *  and routes the child to age-appropriate, not-yet-secured work.
 *
 *  Unlike the age gate this makes NO age assumption: a prerequisite is treated
 *  as covered only because the child demonstrably secured a skill that depends
 *  on it. Pure; reads mastery via the injected accessor; never persists.
 *  Empty / no-secured-skills input → empty set (a safe no-op). */
export function securePrereqClosureSkillIds(
  masteryBandFor: (id: string) => string | undefined,
): string[] {
  const secured = (id: string): boolean => {
    const mb = masteryBandFor(id);
    return mb === "secure" || mb === "fluent";
  };
  const covered = new Set<string>();
  const stack: string[] = [];
  for (const s of SKILLS) {
    if (secured(s.id)) for (const p of s.prerequisites) stack.push(p);
  }
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (covered.has(id) || !BY_ID[id]) continue;
    covered.add(id);
    for (const p of BY_ID[id].prerequisites) stack.push(p);
  }
  // Never re-mask a skill the child has their OWN real mastery for — it keeps
  // its genuine state in the walk; we only cover the implied-done prerequisites.
  return [...covered].filter((id) => !secured(id));
}


/** Human label for a microSkill id (falls back to the id itself). */
export function skillLabel(skillId: string): string {
  return BY_ID[skillId]?.label ?? skillId;
}
